import type { BrowserHistoryArgs } from 'instantsearch.js/es/lib/routers/history';
import type { RouterProps } from 'instantsearch.js/es/middlewares/createRouterMiddleware';
import type { UiState, IndexUiState } from 'instantsearch.js/es/types';

import singletonRouter from 'next/router';
import { useCallback, useRef } from 'react';
import { createInstantSearchRouterNext } from 'react-instantsearch-router-nextjs';

import { useAlgoliaSortingOptions } from './useAlgoliaSortingOptions';
import { PageTypes } from '~/utilities/constants';
import {
  QUERY_FALLBACK,
  PERSONALISED_PRICE,
  PRICE,
  HIDDEN_URL_FACETS,
} from '~/utilities/constants/algolia';
import { useUserContext } from '~/utilities/context/dynamic/UserContext';
import { useAppContext } from '~/utilities/context/static/AppContext';
import { useProductListContext } from '~/utilities/context/static/ProductListContext';
import { useStaticContext } from '~/utilities/context/static/StaticContext';
import { getHiddenDiscountFacet } from '~/utilities/helpers/getHiddenDiscountFacet';
import { solrFacetsToAlgoliaFacets } from '~/utilities/parsers/solrFacetsToAlgoliaFacets';
import { isPLP } from '~/utilities/ssr';

export interface AlgoliaRouterState {
  /**
   * legacy fallback for `q` param
   *
   * From Confluence:
   * > a possibility when you go directly to that URL, but nowhere actually used
   */
  terms?: string;
  q?: string;
  page?: number;
  sort?: string;

  // Fit/Style Filters
  fits?: string;
  style?: string;
}

const nonFacetParams = new Set(['sort', 'page', 'q', 'siteType']);

/**
 * Custom routerToState and stateToRouter function to keep in sync algolia state and SEO friendly URLS
 *
 * All usage of `categoryDetails` must use instead `stableCategoryDetails` because of how algolia instantiates router module
 *
 * https://www.algolia.com/doc/guides/building-search-ui/going-further/routing-urls/react/#example-of-implementation
 */
export const useAlgoliaRouterSync = (): RouterProps<UiState, AlgoliaRouterState> | undefined => {
  const { locale, lang, pageType } = useAppContext();
  const {
    configuration: { membersOnlyPriceEnabled, l30DPricesEnabled },
  } = useStaticContext();
  const { primaryIndex, sortingOptions, defaultSortingSuffix, getIndexName } =
    useAlgoliaSortingOptions();
  const { stableCategoryDetails, stableIsCustomSOLRAssortmentCategory } = useProductListContext();
  const { loggedInCookie } = useUserContext();
  const searchPagePath = `/${locale}/search`;
  const membersOnlyFeature = membersOnlyPriceEnabled && loggedInCookie;

  const createURL: BrowserHistoryArgs<AlgoliaRouterState>['createURL'] = useCallback(
    ({ routeState, location: { origin, pathname, hash, search } }) => {
      const nextStatePathname = stableCategoryDetails?.current?.requestUrl || pathname;
      const path = pageType === PageTypes.SEARCH ? searchPagePath : nextStatePathname;
      const url = new URL(`${origin}${path}${hash}`);

      // query params that are not related to algolia search
      const extraneousQueryParams = Array.from(new URLSearchParams(search).entries()).filter(
        ([key]) =>
          !stableCategoryDetails?.current?.visibleFacetsList.has(key) &&
          !HIDDEN_URL_FACETS.has(key) &&
          !nonFacetParams.has(key)
      );

      [...Object.entries(routeState), ...extraneousQueryParams].forEach(([key, value]) => {
        if (value) {
          url.searchParams.set(key, value);
        }
      });

      stableCategoryDetails?.current?.selectedFacetsV2?.forEach(facet => {
        if (facet?.code && facet?.facetValues?.length) {
          type facetValueType = string | undefined;

          const algoliaFacetValue = routeState[
            facet.code as keyof AlgoliaRouterState
          ] as facetValueType;

          // facet value gets serialized by .join(',') if there is more than one value
          // so if there is no ',' in the facet value, we remove it from query params
          // because it is already applied as url path
          if (!algoliaFacetValue?.includes(',')) {
            url.searchParams.delete(facet.code);
          }
        }
      });

      return url.toString();
    },
    [stableCategoryDetails, pageType, searchPagePath]
  );

  const parseURL: BrowserHistoryArgs<AlgoliaRouterState>['parseURL'] = useCallback(
    ({ qsModule, location }) => {
      const queryParams = qsModule.parse(location.search.slice(1));

      // Persist style/fit in the url not as query param
      if (isPLP(location.pathname) && stableCategoryDetails?.current?.selectedFacetsV2) {
        const solrFacets = solrFacetsToAlgoliaFacets(
          stableCategoryDetails?.current?.selectedFacetsV2
        );

        if (stableIsCustomSOLRAssortmentCategory.current) {
          return queryParams;
        }

        return {
          ...queryParams,
          ...solrFacets,
        };
      }

      return queryParams;
    },
    [stableCategoryDetails, stableIsCustomSOLRAssortmentCategory]
  );

  const stateToRoute = useCallback(
    (uiState: UiState): AlgoliaRouterState => {
      const indexUiState: IndexUiState = uiState[primaryIndex!];

      if (!indexUiState) {
        return {};
      }

      // relevance is the primary index itself, so `sortBy` is undefined while it is selected
      const sortBySuffix = sortingOptions.find(
        ({ value }) => value === (indexUiState.sortBy ?? primaryIndex)
      )?.suffix;

      const refinementList = Object.keys(indexUiState?.refinementList || {}).reduce(
        (accumulator, key) => {
          let [flatKey] = key.split('.');

          // creates a stable shareable link regardless loggedIn status
          if (flatKey === PERSONALISED_PRICE) {
            flatKey = PRICE;
          }

          accumulator[flatKey] = indexUiState?.refinementList?.[key]?.join(',');

          return accumulator;
        },
        {} as { [attribute: string]: string | undefined }
      );

      const partialState: Record<string, unknown> = {};

      if (indexUiState.page) {
        partialState.page = indexUiState.page;
      }

      if (indexUiState.query) {
        partialState.q = indexUiState.query;
      }

      // the category default sorting is implied by the absence of the `sort` param
      if (sortBySuffix && sortBySuffix !== defaultSortingSuffix) {
        partialState.sort = sortBySuffix;
      }

      return {
        ...partialState,
        ...refinementList,
      };
    },
    [primaryIndex, sortingOptions, defaultSortingSuffix]
  );

  const routeToState = useCallback(
    (routeState: AlgoliaRouterState): UiState => {
      // a missing, empty (`?sort=`) or not offered sort param falls back to the category default
      const isOfferedSorting = sortingOptions.some(({ suffix }) => suffix === routeState.sort);
      const sortByIndex = getIndexName(isOfferedSorting ? routeState.sort : defaultSortingSuffix);

      const visibleFacetsList = stableCategoryDetails?.current?.visibleFacetsList;
      // a shared link with the hidden discount facet must not apply a filter the user cannot see
      const hiddenDiscountFacet = getHiddenDiscountFacet(l30DPricesEnabled, visibleFacetsList);
      const routerStateKeys = Object.keys(routeState).filter(
        key =>
          (visibleFacetsList?.has(key) && key !== hiddenDiscountFacet) || HIDDEN_URL_FACETS.has(key)
      );
      const refinementList: Record<string, string[] | undefined> = {};

      routerStateKeys.forEach(routerStateKey => {
        let key = routerStateKey;
        const facet =
          stableCategoryDetails?.current?.visibleFacetsList.get(key) ?? HIDDEN_URL_FACETS.get(key);

        // restore correct state for loggedIn users for price/personalisedPrice filtering
        if (membersOnlyFeature && key === PRICE) {
          key = PERSONALISED_PRICE;
        }

        const refinementKey = facet?.localized ? `${key}.${lang}` : key;

        refinementList[refinementKey] = routeState?.[routerStateKey as keyof AlgoliaRouterState]
          ?.toString()
          ?.split(',');
      });

      const queryBytes = new Blob([routeState.q || '']).size;

      return {
        [primaryIndex!]: {
          // Algolia limits the length size of the query to be under 500 bytes.
          query: queryBytes >= 500 ? QUERY_FALLBACK : routeState.q ?? routeState.terms,
          page: routeState.page,
          sortBy: sortByIndex,
          refinementList,
        } as UiState,
      };
    },
    [
      stableCategoryDetails,
      membersOnlyFeature,
      l30DPricesEnabled,
      sortingOptions,
      defaultSortingSuffix,
      getIndexName,
      lang,
      primaryIndex,
    ]
  );

  const algoliaRouterSync = useRef({
    router: createInstantSearchRouterNext({
      singletonRouter,
      routerOptions: {
        writeDelay: 0,
        /**
         * `useInstantSearchApi()` disposes the search instance on a timer, after Next has already
         * rendered the page the user navigated to. Cleaning the URL on dispose would rewrite that
         * page's URL back to the lister the user has just left. `false` becomes the library default
         * in its next major version.
         */
        cleanUrlOnDispose: false,
        createURL,
        parseURL,
        push(url) {
          /**
           * This is required to prevent scrolling to the top of the page for PLP/SRP pages
           * when users interact with filters, sorting and pagination
           *
           * Resetting scroll position is needed for sorting and filter changes
           * but should not reset the scroll position on pagination changes(load next/load previous)
           */
          const hasPageQueryParam = url.includes('page');

          singletonRouter.push(url, url, {
            shallow: true,
            scroll: false,
          });

          if (!hasPageQueryParam) {
            requestAnimationFrame(() => {
              document
                .querySelector('[data-plp-section]')
                ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            });
          }
        },
      },
    }),
    stateMapping: {
      stateToRoute,
      routeToState,
    },
  });

  // Keep state mapping fresh when dependencies like primaryIndex change at runtime.
  algoliaRouterSync.current.stateMapping.stateToRoute = stateToRoute;
  algoliaRouterSync.current.stateMapping.routeToState = routeToState;

  return algoliaRouterSync.current;
};
